import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { adminLogin } from '@/lib/api';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminLoginPage() {
    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // If already authenticated, redirect to dashboard
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/admin/dashboard');
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Introduce email y contraseña.');
            return;
        }
        setLoading(true);
        try {
            const res = await adminLogin({ email, password });
            login(res.data.token, { email: res.data.email, role: res.data.role });
            toast.success('Sesión iniciada correctamente.');
            navigate('/admin/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Credenciales incorrectas.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4" data-testid="admin-login-page">
            <div className="w-full max-w-sm">
                <div className="text-center mb-6">
                    <img src="/images/asg-logo.png" alt="ASG Motor" className="h-14 w-auto mx-auto mb-3" />
                    <h1 className="font-[var(--font-display)] text-xl font-semibold">Panel de Administración</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Accede para gestionar tu inventario.</p>
                </div>

                <Card className="p-5 rounded-xl">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-sm">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Tu email"
                                className="mt-1"
                                data-testid="admin-login-email-input"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-sm">Contraseña</Label>
                            <div className="relative mt-1">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Tu contraseña"
                                    data-testid="admin-login-password-input"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <Button type="submit" disabled={loading} className="w-full bg-[var(--brand-navy)] hover:bg-[#0d3560] text-white gap-2" data-testid="admin-login-submit-button">
                            <Shield className="h-4 w-4" />
                            {loading ? 'Iniciando sesión...' : 'Entrar'}
                        </Button>
                    </form>
                </Card>

                <div className="text-center mt-4">
                    <Link to="/" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[var(--brand-blue)] inline-flex items-center gap-1">
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Volver a la web
                    </Link>
                </div>
            </div>
        </div>
    );
}
